import type { QueryKey } from '@tanstack/react-query';
import { z } from 'zod';

import { apiListQuery, apiQuery } from '@/shared/data';
import type { Page } from '@/shared/http';
import { getDefaultPermissionList, type CatalogEntry } from '@/shared/permissions';
import type { Permission } from '@/shared/screen';

/** Forma do dado: `page` devolve `Page<item>`, `series` uma lista, `record` e `scalar` o valor. */
export type DataShape = 'page' | 'record' | 'series' | 'scalar';

/** Piso de sensibilidade da fonte; campo a campo, a PII vai no wire com `.meta({ pii })`. */
export type Sensitivity = 'public' | 'internal' | 'restricted';

/** O que o `fetch` recebe além dos params: o `signal` do TanStack Query e o timeout declarado. */
export interface DataSourceContext {
  readonly signal: AbortSignal;
  readonly timeoutMs?: number;
}

export interface DataSourceLimits {
  /** Quanto tempo o dado vale no cache antes de refazer (vira `staleTime`). */
  readonly staleTimeMs: number;
  /** Timeout da chamada; o `*.api.ts` repassa para o axios. */
  readonly timeoutMs?: number;
  /** Teto de `size` que o agente pode pedir (08 §4.4, limite 5). */
  readonly maxSize?: number;
}

/** Dado entregue pela fonte, pela forma: `page` usa o `response` como schema do item. */
export type ShapeData<S extends DataShape, T> = S extends 'page' ? Page<T> : S extends 'series' ? readonly T[] : T;

interface DataSourceFields<S extends DataShape, P extends z.ZodType, R extends z.ZodType> {
  /** Estável (`fund.list`); renomear é versão nova do catálogo. */
  readonly id: string;
  /** Inglês canônico: é o que o modelo lê para escolher a fonte (U33). */
  readonly description: string;
  readonly shape: S;
  /** Só o que o agente pode mexer; é zod e vira JSON Schema. */
  readonly params: P;
  /** Zod do `*.wire.ts` (item, no caso de `page`). */
  readonly response: R;
  readonly sensitivity: Sensitivity;
  readonly limits: DataSourceLimits;
  /** Chave de cache; passe a fábrica do módulo para dividir o cache com a tela oficial. */
  key?(params: z.output<P>): QueryKey;
  /** Reusa o `*.api.ts` do módulo. Função, nunca URL: o documento da página só referencia o `id`. */
  fetch(params: z.output<P>, context: DataSourceContext): Promise<ShapeData<S, z.output<R>>>;
}

type PermissionDeclaration =
  | { readonly permission: Permission; readonly reason?: never }
  | { readonly permission: 'none'; readonly reason: string };

/** Fonte de dados de leitura do catálogo (08 §4.1). `permission` é obrigatória (09 §5.3). */
export type DataSource<S extends DataShape = DataShape, P extends z.ZodType = z.ZodType, R extends z.ZodType = z.ZodType> = DataSourceFields<
  S,
  P,
  R
> &
  PermissionDeclaration;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- registro mistura fontes de params e respostas diferentes
export type AnyDataSource = DataSource<DataShape, any, any>;

/** O dado que a fonte entrega, já na forma (`Page<item>` para `page`). */
export type SourceData<D> = D extends DataSource<infer S, z.ZodType, infer R> ? ShapeData<S, z.output<R>> : never;

/** Declara uma fonte. Em dev, fonte inválida lança erro, como o `defineScreen`. */
export function defineDataSource<S extends DataShape, P extends z.ZodType, R extends z.ZodType>(source: DataSource<S, P, R>): DataSource<S, P, R> {
  if (import.meta.env.DEV) {
    const issues = validateDataSource(source);
    if (issues.length > 0) throw new Error(`fonte ${source.id}: ${issues.join('; ')}`);
  }
  return source;
}

export const CATALOG_ID = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/;
const CRUD = { Read: 'readPermission', Create: 'createPermission', Update: 'updatePermission', Delete: 'deletePermission' } as const;
const SHAPES: readonly DataShape[] = ['page', 'record', 'series', 'scalar'];
const SENSITIVITIES: readonly Sensitivity[] = ['public', 'internal', 'restricted'];

/** Texto para o modelo: inglês canônico, sem acento, com tamanho para ele escolher. */
export function descriptionIssues(owner: string, description: string): string[] {
  const issues: string[] = [];
  if (description.trim().length < 12) issues.push(`${owner}: description curta demais para um modelo escolher`);
  if (/[À-ÿ]/.test(description)) issues.push(`${owner}: description em inglês canônico (U33)`);
  return issues;
}

/** A permissão existe no catálogo do front: recurso conhecido e tipo que o recurso admite. */
export function permissionIssues(owner: string, permission: Permission, catalog: readonly CatalogEntry[] = getDefaultPermissionList()): string[] {
  const entry = catalog.find((item) => item.resource === permission.resource);
  if (!entry) return [`${owner}: recurso ${permission.resource} fora do catálogo de permissões`];
  const crudKey = Object.hasOwn(CRUD, permission.action) ? CRUD[permission.action as keyof typeof CRUD] : undefined;
  const known = crudKey ? entry[crudKey] !== undefined : entry.actions !== undefined && Object.hasOwn(entry.actions, permission.action);
  return known ? [] : [`${owner}: ${permission.resource} não tem ${permission.action} no catálogo (fail-closed, 09 §5.3)`];
}

/** Parâmetros serializáveis: objeto zod que vira JSON Schema sem erro. */
export function paramsIssues(owner: string, params: z.ZodType): string[] {
  if (!(params instanceof z.ZodObject)) return [`${owner}: params precisa ser z.object`];
  try {
    z.toJSONSchema(params);
    return [];
  } catch (error) {
    return [`${owner}: params não vira JSON Schema (${error instanceof Error ? error.message : String(error)})`];
  }
}

export function validateDataSource(source: AnyDataSource): string[] {
  const owner = `fonte ${source.id}`;
  const issues: string[] = [];
  if (!CATALOG_ID.test(source.id)) issues.push(`${owner}: id inválido (use domínio.nome)`);
  issues.push(...descriptionIssues(owner, source.description));
  if (!SHAPES.includes(source.shape)) issues.push(`${owner}: shape inválido`);
  if (!SENSITIVITIES.includes(source.sensitivity)) issues.push(`${owner}: sensitivity inválida`);
  if (source.permission === 'none') {
    if (typeof source.reason !== 'string' || source.reason.trim().length < 12) issues.push(`${owner}: permission 'none' exige reason`);
  } else if (typeof source.permission !== 'object') {
    issues.push(`${owner}: permission é obrigatória (09 §5.3)`);
  } else {
    issues.push(...permissionIssues(owner, source.permission));
  }
  issues.push(...paramsIssues(owner, source.params as z.ZodType));
  if (!(source.response instanceof z.ZodType)) issues.push(`${owner}: response precisa ser zod`);
  if (typeof source.fetch !== 'function') issues.push(`${owner}: fetch precisa ser função`);
  if ('url' in source || 'path' in source) issues.push(`${owner}: fonte não carrega URL; o fetch reusa o *.api.ts`);
  if (!(source.limits.staleTimeMs >= 0)) issues.push(`${owner}: limits.staleTimeMs inválido`);
  if (source.limits.timeoutMs !== undefined && !(source.limits.timeoutMs > 0)) issues.push(`${owner}: limits.timeoutMs inválido`);
  return issues;
}

/** Chave de cache da fonte: a do módulo, se declarada; senão `[domínio, 'source', id, params]`. */
export function dataSourceKey(source: AnyDataSource, params: unknown): QueryKey {
  return source.key ? source.key(params) : [source.id.split('.')[0] ?? source.id, 'source', source.id, params];
}

export type SourceFetch = (source: AnyDataSource, params: unknown, context: DataSourceContext) => Promise<unknown>;

const directFetch: SourceFetch = (source, params, context) => source.fetch(params, context);

/**
 * `queryOptions` da fonte (`apiListQuery` para `page`, `apiQuery` no resto), com a mesma chave que
 * a tela oficial usa. `params` passa pelo zod da fonte, com os defaults.
 */
export function dataSourceQuery<D extends AnyDataSource>(source: D, params: z.input<D['params']>, run: SourceFetch = directFetch) {
  const parsed: unknown = (source.params as z.ZodType).parse(params);
  const timeoutMs = source.limits.timeoutMs;
  const fetcher = ({ signal }: { signal: AbortSignal }) =>
    run(source, parsed, timeoutMs === undefined ? { signal } : { signal, timeoutMs }) as Promise<SourceData<D>>;
  const extra = { staleTime: source.limits.staleTimeMs };
  return source.shape === 'page' ? apiListQuery(dataSourceKey(source, parsed), fetcher, extra) : apiQuery(dataSourceKey(source, parsed), fetcher, extra);
}

/** Vazio pela forma: página ou série sem item; registro ou escalar nulo. */
export function isEmptyShape(shape: DataShape, data: unknown): boolean {
  if (data === null || data === undefined) return true;
  if (shape === 'page') return (data as Page<unknown>).items.length === 0;
  if (shape === 'series') return (data as readonly unknown[]).length === 0;
  return false;
}
